import { caseStudies } from '@/content/caseStudies';
import { profile } from '@/content/profile';
import { siteConfig } from '@/seo/siteConfig';

export interface RouteMeta {
  path: string;
  /** Full document title, already expanded from the template. */
  title: string;
  description: string;
  /** Excluded from the sitemap and marked `noindex`. */
  noIndex?: boolean;
}

function meta(path: string, title: string, description: string): RouteMeta {
  return { path, title: siteConfig.titleTemplate(title), description };
}

/**
 * Metadata for every public route.
 *
 * This module is the single source used by the `Seo` component at runtime, by
 * the prerender step at build time, and by the sitemap generator — so a route
 * cannot ship with a title in one place and a different one in another.
 */
export const staticRouteMeta: readonly RouteMeta[] = [
  {
    path: '/',
    title: siteConfig.defaultTitle,
    description: siteConfig.defaultDescription,
  },
  meta(
    '/about',
    'About',
    `How ${profile.name} approaches frontend architecture, accessibility, performance and technical leadership, with education, certifications and languages.`,
  ),
  meta(
    '/experience',
    'Experience',
    'Twenty years of software development: enterprise AI tooling at IBM, ten years of product delivery at AROBS, and earlier public-sector and freelance work.',
  ),
  meta(
    '/case-studies',
    'Case studies',
    'Four engineering case studies covering an enterprise AI code transformation platform, a nationwide disaster alert platform, a telehealth product and a multi-tenant booking SaaS, plus PersonaReply, a personal generative-AI project.',
  ),
  meta(
    '/contact',
    'Contact',
    'Get in touch about senior frontend, React or full-stack opportunities. Include the role, location model and expected start date.',
  ),
  meta(
    '/cv',
    'CV',
    `Print-friendly curriculum vitae for ${profile.name}, Senior Frontend Engineer specializing in React, TypeScript and Next.js.`,
  ),
  meta(
    '/privacy',
    'Privacy notice',
    'How messages submitted through the contact form on this site are processed and stored.',
  ),
];

export const caseStudyRouteMeta: readonly RouteMeta[] = caseStudies.map((caseStudy) =>
  meta(`/case-studies/${caseStudy.slug}`, caseStudy.title, caseStudy.cardSummary),
);

export const allRouteMeta: readonly RouteMeta[] = [...staticRouteMeta, ...caseStudyRouteMeta];

export const notFoundMeta: RouteMeta = {
  path: '/404',
  title: siteConfig.titleTemplate('Page not found'),
  description: 'The page you were looking for does not exist.',
  noIndex: true,
};

export function getRouteMeta(pathname: string): RouteMeta {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.replace(/\/+$/, '') : pathname;

  return allRouteMeta.find((entry) => entry.path === normalized) ?? notFoundMeta;
}
